import type { ReviewGrid, ReviewOperation, SessionReview } from '@matter_extractor/shared';
import { createContext, useContext } from 'react';

/**
 * What every part of the review page needs of the review and of the gestures
 * (relecture.md §4.4): the review as it stands, whether it may be written, and
 * the ONE way to write it.
 *
 * A context rather than props through six levels: the case, its extracts, the
 * popover and the history all read the same review and send the same
 * operations, and a prop that crosses four components untouched is a prop four
 * tests have to supply. `ReviewScreen` is the only provider.
 */
export interface ReviewApi {
  readonly sessionId: string;
  readonly review: SessionReview;
  /** The frozen grid, or the one the first gesture will freeze; null without any target (§5.1). */
  readonly grid: ReviewGrid | null;
  /** The objective ids of the grid, in the protocol's order - the case numbers read off it. */
  readonly order: readonly string[];
  /** The review may be written now: finished, not in the trash (§3.1, §3.4). */
  readonly canWrite: boolean;
  /** A gesture is on its way; a second one waits for it. */
  readonly busy: boolean;
  /**
   * Sends the gestures, each in its own transaction on the server, and reads the
   * review again from the answer. True when every one was applied; a refusal is
   * said by the page itself.
   */
  readonly apply: (operations: readonly ReviewOperation[]) => Promise<boolean>;
}

const ReviewContext = createContext<ReviewApi | null>(null);

export const ReviewProvider = ReviewContext.Provider;

export function useReviewApi(): ReviewApi {
  const api = useContext(ReviewContext);
  if (api === null) throw new Error('useReviewApi must be used inside <ReviewProvider>');
  return api;
}
